import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import { getClientConfig } from '../lib/config';
import type { AuthProvider } from '../lib/types';

/**
 * Dedicated sign-in page. The auth gate in App.tsx redirects every unauthenticated request
 * here, so this is a full-screen page (not the app shell) that makes it unambiguous the user
 * must sign in before using the app.
 *
 * Server-config-driven: in Identity mode it posts to POST /api/auth/login (ASP.NET Identity
 * cookie login); in Okta/Entra mode it renders a single "Sign in with {IdP}" button that
 * starts an OIDC Authorization Code + PKCE redirect.
 */
export function Login() {
  const [provider, setProvider] = useState<AuthProvider | null>(null);

  useEffect(() => {
    void getClientConfig().then(cfg => setProvider(cfg.authProvider));
  }, []);

  if (provider === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }

  return provider === 'Identity' ? <IdentityLogin /> : <OidcLogin provider={provider} />;
}

function IdentityLogin() {
  const [email, setEmail] = useState('admin@localhost');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post('/api/auth/login', { email, password });
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/');
    } catch (ex) {
      setErr(ex instanceof ApiError && ex.status === 401 ? 'Invalid credentials.' : 'Login failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <h1 className="text-xl font-bold text-atg-navy mb-4">Sign in</h1>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email" required value={email} onChange={e => setEmail(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-atg-blue"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
          <input
            type="password" required value={password} onChange={e => setPassword(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-atg-blue"
          />
        </div>
        {err && <div className="text-red-600 text-sm">{err}</div>}
        <button
          type="submit" disabled={busy}
          className="w-full rounded-md bg-atg-navy text-white py-2 text-sm font-medium hover:bg-atg-blue disabled:opacity-50"
        >
          {busy ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </Shell>
  );
}

/** Reasons the server-side OIDC handshake can bounce back to /login (see OnRemoteFailure). */
const OIDC_ERRORS: Record<string, string> = {
  not_assigned:
    "Your account isn't assigned to this application in your identity provider. Ask an " +
    'administrator to grant you access.',
  signin_failed: 'Sign-in with your organization account failed. Please try again.',
};

function OidcLogin({ provider }: { provider: AuthProvider }) {
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const label = provider === 'Okta' ? 'Okta' : provider === 'Entra' ? 'Entra' : provider;
  const errorKey = searchParams.get('error');
  const err = errorKey ? (OIDC_ERRORS[errorKey] ?? OIDC_ERRORS.signin_failed) : null;

  function signIn() {
    setBusy(true);
    // Server-side OIDC: the API challenges the IdP, completes the code exchange at
    // /signin-oidc, sets the auth cookie, and redirects back here. Must be a top-level
    // navigation, not an XHR.
    window.location.assign('/api/auth/challenge?returnUrl=%2F');
  }

  return (
    <Shell>
      <h1 className="text-xl font-bold text-atg-navy mb-4">Sign in</h1>
      <p className="text-sm text-slate-600 mb-4">
        Use your organization account to access the app.
      </p>
      {err && <div role="alert" className="text-red-700 text-sm mb-4">{err}</div>}
      <button
        type="button" onClick={signIn} disabled={busy}
        className="w-full rounded-md bg-atg-navy text-white py-2 text-sm font-medium hover:bg-atg-blue disabled:opacity-50"
      >
        {busy ? 'Redirecting...' : `Sign in with ${label}`}
      </button>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-2xl font-bold text-atg-navy">DevSpend</div>
          <p className="text-slate-600 mt-2 text-sm">Please sign in to continue.</p>
        </div>
        <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
